import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Contratos reais (backend/.review-loop/reports/api-contracts.md §5) ──
 * GET /finance/dashboards/summary     → DashboardSummary
 * GET /finance/dashboards/cash-flow   → CashFlowResponse (query periodStart/periodEnd)
 * GET /finance/dashboards/dre         → DreResponse (query periodStart/periodEnd)
 * GET /finance/payables               → {data,total} (query page/pageSize/sortBy/sortOrder)
 * GET /finance/receivables            → {data,total} (idem)
 * GET /finance/payables/aging         → AgingResponse (⚠️ sombreada por /:id — §2.3)
 * GET /finance/receivables/aging      → AgingResponse (⚠️ idem)
 * NÃO EXISTE GET /tenants/{id}/finance/dashboard (removido — ver A1 no evidence).
 */

interface DashboardSummary {
  totalPayables: number;
  totalReceivables: number;
  cashBalance: number;
  overduePayables: number;
  overdueReceivables: number;
  shortTermPayables: number;
  shortTermReceivables: number;
  netShortTermPosition: number;
  overduePayablesCount: number;
  overdueReceivablesCount: number;
}

interface CashFlowEntry {
  period: string;
  periodStart: string;
  periodEnd: string;
  projectedInflows: number;
  projectedOutflows: number;
  netCashFlow: number;
  closingBalance: number;
}

interface CashFlowResponse {
  entries: CashFlowEntry[];
  totalInflows: number;
  totalOutflows: number;
  netProjected: number;
}

interface AgingBucket {
  label: string;
  fromDays: number;
  toDays: number;
  total: number;
  count: number;
}

/**
 * Espelha o relatorio que `GET /finance/{payables,receivables}/aging` devolve.
 *
 * A versao anterior declarava `buckets[]`, que o endpoint nunca mandou: o
 * painel caia sempre no calculo local sobre a pagina carregada e mostrava
 * barras erradas com a nota "estimado".
 */
interface AgingItemResponse {
  id: string;
  documentNumber: string;
  dueDate: string;
  daysOverdue: number;
  openBalance: number;
  partyName: string;
  category: string;
}

interface AgingResponse {
  current: number;
  days30: number;
  days60: number;
  days90: number;
  days120plus: number;
  total: number;
  items: AgingItemResponse[];
}

/** Faixas do relatorio do servidor; a contagem sai de `items`, nao de estimativa. */
function bucketsFromReport(report: AgingResponse): AgingBucket[] {
  const bands: AgingBucket[] = [
    { label: 'A vencer', fromDays: -9999, toDays: 0, total: report.current, count: 0 },
    { label: '1\u201330 dias', fromDays: 1, toDays: 30, total: report.days30, count: 0 },
    { label: '31\u201360 dias', fromDays: 31, toDays: 60, total: report.days60, count: 0 },
    { label: '61\u201390 dias', fromDays: 61, toDays: 90, total: report.days90, count: 0 },
    { label: '90+ dias', fromDays: 91, toDays: 99999, total: report.days120plus, count: 0 },
  ];
  for (const item of report.items ?? []) {
    const overdue = item.daysOverdue;
    const idx = overdue <= 0 ? 0 : 1 + Math.min(3, Math.floor((overdue - 1) / 30));
    const band = bands[idx];
    if (band) band.count += 1;
  }
  return bands;
}

interface DreEntry {
  label: string;
  value: number;
  level: number;
  isSubtotal: boolean;
}

interface DreResponse {
  periodStart: string;
  periodEnd: string;
  entries: DreEntry[];
  netResult: number;
}

interface PayableItem {
  id: string;
  supplierName: string;
  documentNumber: string;
  dueDate: string;
  originalAmount: number;
  openBalance: number;
  status: string;
  category: string;
}

interface ReceivableItem {
  id: string;
  customerName: string;
  documentNumber: string;
  dueDate: string;
  originalAmount: number;
  openBalance: number;
  status: string;
  category: string;
}

interface Paged<T> {
  data: T[];
  total: number;
}

type Side = 'pay' | 'receive';
type Granularity = 'weekly' | 'monthly';
type Tab = 'overview' | 'dre';

interface FlowPoint {
  label: string;
  inflows: number;
  outflows: number;
}

/* ── Helpers ── */

function toISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysUntil(due: string, now: Date): number {
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return 0;
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((a - b) / 86400000);
}

function relativeLabel(days: number): string {
  if (days === 0) return 'vence hoje';
  if (days === 1) return 'vence amanhã';
  if (days > 1) return `vence em ${days} dias`;
  if (days === -1) return 'venceu ontem';
  return `vencido há ${Math.abs(days)} dias`;
}

const STATUS_META: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
  OPEN: { variant: 'info', label: 'Em aberto' },
  PARTIALLY_SETTLED: { variant: 'warning', label: 'Parcial' },
  OVERDUE: { variant: 'error', label: 'Vencido' },
  SETTLED: { variant: 'success', label: 'Liquidado' },
  RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
  CANCELLED: { variant: 'neutral', label: 'Cancelado' },
};

function statusMeta(status: string): { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string } {
  return STATUS_META[status] ?? { variant: 'neutral', label: status };
}

/** Fallback rotulado quando /aging responde 404 (sombreamento Express §2.3). */
function estimateAging(items: Array<{ dueDate: string; openBalance: number }>, now: Date): AgingBucket[] {
  const acc = [
    { label: 'A vencer', fromDays: -9999, toDays: -1, total: 0, count: 0 },
    { label: '1–30 dias', fromDays: 1, toDays: 30, total: 0, count: 0 },
    { label: '31–60 dias', fromDays: 31, toDays: 60, total: 0, count: 0 },
    { label: '61–90 dias', fromDays: 61, toDays: 90, total: 0, count: 0 },
    { label: '90+ dias', fromDays: 91, toDays: 99999, total: 0, count: 0 },
  ];
  for (const item of items) {
    const overdue = -daysUntil(item.dueDate, now);
    const amount = Number(item.openBalance) || 0;
    const bucket = overdue <= 0 ? acc[0] : acc[1 + Math.min(3, Math.floor((overdue - 1) / 30))];
    if (bucket) {
      bucket.total += amount;
      bucket.count += 1;
    }
  }
  return acc;
}

function groupFlow(entries: CashFlowEntry[], gran: Granularity): FlowPoint[] {
  if (gran === 'monthly') {
    const byMonth = new Map<string, FlowPoint>();
    for (const e of entries) {
      const key = new Date(e.periodStart).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      const cur = byMonth.get(key) ?? { label: key, inflows: 0, outflows: 0 };
      cur.inflows += e.projectedInflows;
      cur.outflows += e.projectedOutflows;
      byMonth.set(key, cur);
    }
    return [...byMonth.values()];
  }
  const weeks = new Map<number, FlowPoint>();
  const start = entries.length > 0 ? new Date(entries[0]?.periodStart ?? Date.now()).getTime() : Date.now();
  for (const e of entries) {
    const idx = Math.max(0, Math.floor((new Date(e.periodStart).getTime() - start) / (7 * 86400000)));
    const cur = weeks.get(idx) ?? { label: `Semana ${idx + 1}`, inflows: 0, outflows: 0 };
    cur.inflows += e.projectedInflows;
    cur.outflows += e.projectedOutflows;
    weeks.set(idx, cur);
  }
  return [...weeks.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
}

function toCSV(rows: Array<Record<string, string>>): string {
  const escape = (v: string): string => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0] ?? {});
  const lines = [headers.map(escape).join(';')];
  for (const r of rows) lines.push(headers.map((h) => escape(r[h] ?? '')).join(';'));
  return '\ufeff' + lines.join('\n');
}

/* ── SVG charts (inline, sem nova dependência) ── */

function FlowChart({ points }: { points: FlowPoint[] }): JSX.Element {
  const W = 640;
  const H = 260;
  /* Espaco a esquerda para o rotulo da linha de grade ("R$ 54.800,00"). Com
     padL=8 o rotulo so cabia na borda direita, atras das barras. */
  const padL = 96;
  const padR = 12;
  const padB = 28;
  const padT = 30;
  const max = Math.max(1, ...points.map((p) => Math.max(p.inflows, p.outflows)));
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const slot = innerW / Math.max(1, points.length);
  const barW = Math.min(30, Math.max(12, slot / 3.2));
  const gridLines = [0.25, 0.5, 0.75, 1];

  return (
    <div className="exec-chart-scroll">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="exec-chart"
        role="img"
        aria-label={`Fluxo projetado: entradas ${formatCurrency(points.reduce((a, p) => a + p.inflows, 0))} e saídas ${formatCurrency(points.reduce((a, p) => a + p.outflows, 0))} em ${points.length} períodos.`}
      >
        {gridLines.map((f) => {
          const y = padT + innerH * (1 - f);
          return (
            <g key={f}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} className="exec-grid" />
              {/* Na borda direita o rótulo ficava ATRÁS das barras das últimas
                  semanas. À esquerda do plot não há barra nenhuma. */}
              <text x={padL - 6} y={y - 4} textAnchor="end" className="exec-grid-label">
                {formatCurrency(max * f)}
              </text>
            </g>
          );
        })}
        {points.map((p) => {
          const cx = padL + slot * points.indexOf(p) + slot / 2;
          const hi = (p.inflows / max) * innerH;
          const ho = (p.outflows / max) * innerH;
          return (
            <g key={p.label}>
              <title>{`${p.label}: entradas ${formatCurrency(p.inflows)} · saídas ${formatCurrency(p.outflows)}`}</title>
              <rect x={cx - barW - 3} y={padT + innerH - hi} width={barW} height={Math.max(2, hi)} rx={4} className="exec-bar-in" />
              <rect x={cx + 3} y={padT + innerH - ho} width={barW} height={Math.max(2, ho)} rx={4} className="exec-bar-out" />
              <text x={cx} y={H - 8} textAnchor="middle" className="exec-x-label">
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function AgingBars({ buckets, note }: { buckets: AgingBucket[]; note: string }): JSX.Element {
  const W = 640;
  const rowH = 34;
  const H = buckets.length * rowH + 16;
  const max = Math.max(1, ...buckets.map((b) => b.total));
  const labelW = 110;

  return (
    <div className="exec-chart-scroll">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="exec-chart"
        role="img"
        aria-label={`Aging (${note}): ${buckets.map((b) => `${b.label} ${formatCurrency(b.total)}`).join('; ')}.`}
      >
        {buckets.map((b, i) => {
          const y = 8 + i * rowH;
          const w = Math.max(3, ((W - labelW - 150) * b.total) / max);
          return (
            <g key={b.label}>
              <title>
                {b.count > 0
                  ? `${b.label}: ${formatCurrency(b.total)} em ${b.count} títulos`
                  : `${b.label}: ${formatCurrency(b.total)}`}
              </title>
              <text x={0} y={y + 15} className="exec-x-label">
                {b.label}
              </text>
              <rect x={labelW} y={y} width={w} height={18} rx={5} className={b.fromDays >= 1 ? 'exec-bar-out' : 'exec-bar-in'} />
              <text x={labelW + w + 8} y={y + 15} className="exec-grid-label">
                {/* Faixa com valor e contagem ZERO se contradiz na tela: o
                    relatorio de aging nem sempre traz os itens, so os totais. */}
                {formatCurrency(b.total)}
                {b.count > 0 ? ` · ${b.count}` : ''}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Período rápido e situação ────────────────────────────────────────────
   A referência tem "Hoje / Esta Semana / Este Mês" e chips de situação. Os
   dois viram filtro no servidor: `dueDateFrom`/`dueDateTo` e `statusIn`, que
   `PayableFiltersDto` e `ReceivableFiltersDto` sempre aceitaram. */

type PeriodKey = 'today' | 'week' | 'month' | 'all';
type SituationKey = 'open' | 'overdue' | 'settled' | 'all';

/** Rótulo curto do período, para o título do indicador. */
const PERIOD_LABEL: Record<PeriodKey, string> = {
  today: 'hoje',
  week: 'semana',
  month: 'mês',
  all: 'total',
};

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Esta semana' },
  { key: 'month', label: 'Este mês' },
  { key: 'all', label: 'Tudo' },
];

const SITUATIONS: { key: SituationKey; label: string }[] = [
  { key: 'open', label: 'Em aberto' },
  { key: 'overdue', label: 'Vencido' },
  { key: 'settled', label: 'Liquidado' },
  { key: 'all', label: 'Todos' },
];

/* Situação da referência → status do domínio. `all` não manda `statusIn`. */
const SITUATION_STATUSES: Record<SituationKey, readonly string[]> = {
  open: ['OPEN', 'PARTIALLY_SETTLED', 'OVERDUE'],
  overdue: ['OVERDUE'],
  settled: ['SETTLED'],
  all: [],
};

function periodWindow(period: PeriodKey, now: Date): { from?: string; to?: string } {
  if (period === 'all') return {};
  if (period === 'today') {
    const d = toISODate(now);
    return { from: d, to: d };
  }
  if (period === 'week') {
    // semana começando no domingo, como o calendário brasileiro
    const start = new Date(now);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { from: toISODate(start), to: toISODate(end) };
  }
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toISODate(start), to: toISODate(end) };
}

/** Linha da tabela unificada: pagar e receber lado a lado, por vencimento. */
interface TitleRow {
  id: string;
  /** `out` sai do caixa (a pagar), `in` entra (a receber). */
  direction: 'in' | 'out';
  party: string;
  documentNumber: string;
  category: string;
  dueDate: string;
  status: string;
  amount: number;
  original: number;
}

/** Só o que o cartão de conciliação precisa de `GET /finance/reconciliation`. */
interface ReconciliationSummary {
  id: string;
  accountName: string;
  matchedCount: number;
  importedCount: number;
  difference: number;
  status: string;
}

/* ── Page ── */

export function FinanceDashboardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const [side, setSide] = useState<Side>('pay');
  const [gran, setGran] = useState<Granularity>('weekly');
  const [tab, setTab] = useState<Tab>('overview');
  const [filter, setFilter] = useState('');
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [situation, setSituation] = useState<SituationKey>('open');

  const now = useMemo(() => new Date(), []);
  const flowStart = useMemo(() => toISODate(now), [now]);
  const flowEnd = useMemo(() => toISODate(new Date(now.getTime() + 30 * 86400000)), [now]);
  const monthStart = useMemo(() => toISODate(new Date(now.getFullYear(), now.getMonth(), 1)), [now]);

  /* Janela de vencimento do periodo escolhido. Vai para o servidor como
     `dueDateFrom`/`dueDateTo` — os dois filtros sempre existiram em
     `PayableFiltersDto` e `ReceivableFiltersDto`. */
  const dueWindow = useMemo(() => periodWindow(period, now), [period, now]);

  /* Debounce simples: a busca agora vai ao servidor (`q`), entao nao pode
     disparar a cada tecla. */
  const [debouncedFilter, setDebouncedFilter] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter.trim()), 300);
    return () => clearTimeout(t);
  }, [filter]);

  const titleParams = useMemo(() => {
    const p: Record<string, string | readonly string[]> = {
      page: '1',
      pageSize: '50',
      sortBy: 'dueDate',
      sortOrder: 'asc',
    };
    if (dueWindow.from) p['dueDateFrom'] = dueWindow.from;
    if (dueWindow.to) p['dueDateTo'] = dueWindow.to;
    if (debouncedFilter) p['q'] = debouncedFilter;
    const statuses = SITUATION_STATUSES[situation];
    if (statuses.length > 0) p['statusIn'] = statuses;
    return p;
  }, [dueWindow, debouncedFilter, situation]);

  const summaryQ = useQuery<DashboardSummary>({
    queryKey: queryKeys.finance.dashboard(tenantId),
    queryFn: async () => apiClient.get<DashboardSummary>('/finance/dashboards/summary'),
    enabled: !!tenantId,
  });
  const cashFlowQ = useQuery<CashFlowResponse>({
    queryKey: [...queryKeys.finance.dashboard(tenantId), 'cash-flow-30d'],
    queryFn: async () =>
      apiClient.get<CashFlowResponse>('/finance/dashboards/cash-flow', {
        params: { periodStart: flowStart, periodEnd: flowEnd },
      }),
    enabled: !!tenantId,
  });
  const dreQ = useQuery<DreResponse>({
    queryKey: [...queryKeys.finance.dashboard(tenantId), 'dre-month'],
    queryFn: async () =>
      apiClient.get<DreResponse>('/finance/dashboards/dre', {
        params: { periodStart: monthStart, periodEnd: flowStart },
      }),
    enabled: !!tenantId && tab === 'dre',
  });
  const payablesQ = useQuery<Paged<PayableItem>>({
    queryKey: [...queryKeys.finance.payables(), 'dashboard-upcoming', titleParams],
    queryFn: async () =>
      apiClient.get<Paged<PayableItem>>('/finance/payables', { params: titleParams }),
    enabled: !!tenantId,
  });
  const receivablesQ = useQuery<Paged<ReceivableItem>>({
    queryKey: [...queryKeys.finance.receivables(), 'dashboard-upcoming', titleParams],
    queryFn: async () =>
      apiClient.get<Paged<ReceivableItem>>('/finance/receivables', { params: titleParams }),
    enabled: !!tenantId,
  });
  /* O cartao de conciliacao da referencia ("85% conciliado (Itau)") sai da
     conciliacao mais recente: `matchedCount`/`importedCount` passaram a ser
     publicados em T50. */
  const reconQ = useQuery<Paged<ReconciliationSummary>>({
    queryKey: [...queryKeys.finance.reconciliation(), 'dashboard-card'],
    queryFn: async () =>
      apiClient.get<Paged<ReconciliationSummary>>('/finance/reconciliation'),
    enabled: !!tenantId,
    retry: false,
  });
  const payAgingQ = useQuery<AgingResponse>({
    queryKey: [...queryKeys.finance.payables(), 'aging'],
    queryFn: async () => apiClient.get<AgingResponse>('/finance/payables/aging'),
    enabled: !!tenantId,
    retry: false,
  });
  const recAgingQ = useQuery<AgingResponse>({
    queryKey: [...queryKeys.finance.receivables(), 'aging'],
    queryFn: async () => apiClient.get<AgingResponse>('/finance/receivables/aging'),
    enabled: !!tenantId,
    retry: false,
  });

  /* Sem filtro client-side: o que chega ja veio filtrado pelo servidor. */
  const payables = useMemo(() => payablesQ.data?.data ?? [], [payablesQ.data]);
  const receivables = useMemo(() => receivablesQ.data?.data ?? [], [receivablesQ.data]);

  const q = debouncedFilter;
  const filteredPayables = payables;
  const filteredReceivables = receivables;

  /* A referencia mostra UMA tabela com pagar e receber juntos, ordenada por
     vencimento. Sao entidades diferentes, entao a direcao do dinheiro entra
     como coluna — senao um titulo a pagar e um a receber de mesmo valor ficam
     indistinguiveis. */
  const titles = useMemo<TitleRow[]>(() => {
    const rows: TitleRow[] = [
      ...payables.map((p) => ({
        id: p.id,
        direction: 'out' as const,
        party: p.supplierName,
        documentNumber: p.documentNumber,
        category: p.category,
        dueDate: p.dueDate,
        status: p.status,
        amount: p.openBalance,
        original: p.originalAmount,
      })),
      ...receivables.map((r) => ({
        id: r.id,
        direction: 'in' as const,
        party: r.customerName,
        documentNumber: r.documentNumber,
        category: r.category,
        dueDate: r.dueDate,
        status: r.status,
        amount: r.openBalance,
        original: r.originalAmount,
      })),
    ];
    /* Titulo sem vencimento nao pode derrubar o painel inteiro: a ordenacao
       joga o que nao tem data para o fim, em vez de chamar `localeCompare` em
       `undefined`. */
    return rows.sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
  }, [payables, receivables]);

  const totalTitles = (payablesQ.data?.total ?? 0) + (receivablesQ.data?.total ?? 0);

  const inWindow = useMemo(
    () => ({
      receive: receivables.reduce((sum, r) => sum + r.openBalance, 0),
      receiveCount: receivables.length,
      pay: payables.reduce((sum, p) => sum + p.openBalance, 0),
      payCount: payables.length,
    }),
    [payables, receivables],
  );

  const recon = reconQ.data?.data?.[0];
  const reconPct = recon && recon.importedCount > 0
    ? Math.round((recon.matchedCount / recon.importedCount) * 100)
    : null;

  const payAging: { buckets: AgingBucket[]; estimated: boolean } = useMemo(() => {
    if (payAgingQ.data) return { buckets: bucketsFromReport(payAgingQ.data), estimated: false };
    return { buckets: estimateAging(payables, now), estimated: true };
  }, [payAgingQ.data, payables, now]);
  const recAging: { buckets: AgingBucket[]; estimated: boolean } = useMemo(() => {
    if (recAgingQ.data) return { buckets: bucketsFromReport(recAgingQ.data), estimated: false };
    return { buckets: estimateAging(receivables, now), estimated: true };
  }, [recAgingQ.data, receivables, now]);

  const flowPoints = useMemo(
    /* `data?.entries ?? []` NAO e guarda suficiente: se a resposta vier como
       array (erro de contrato, proxy devolvendo lista vazia), `[].entries` e o
       metodo nativo `Array.prototype.entries` — nao e null e nao e undefined —
       e o `??` deixa passar uma FUNCAO para `groupFlow`. A tela morria inteira
       com "entries is not iterable". */
    () => groupFlow(Array.isArray(cashFlowQ.data?.entries) ? cashFlowQ.data.entries : [], gran),
    [cashFlowQ.data, gran],
  );

  const summary = summaryQ.data;

  const overduePayPct = summary && summary.totalPayables > 0 ? (summary.overduePayables / summary.totalPayables) * 100 : 0;
  const overdueRecPct = summary && summary.totalReceivables > 0 ? (summary.overdueReceivables / summary.totalReceivables) * 100 : 0;

  const activeAging = side === 'pay' ? payAging : recAging;
  const overduePct = side === 'pay' ? overduePayPct : overdueRecPct;

  function handleExport(): void {
    const rows =
      side === 'pay'
        ? filteredPayables.map((p) => ({
            contraparte: p.supplierName,
            documento: p.documentNumber,
            categoria: p.category,
            vencimento: formatDate(p.dueDate),
            situacao: statusMeta(p.status).label,
            valor_aberto: String(p.openBalance).replace('.', ','),
          }))
        : filteredReceivables.map((r) => ({
            contraparte: r.customerName,
            documento: r.documentNumber,
            categoria: r.category,
            vencimento: formatDate(r.dueDate),
            situacao: statusMeta(r.status).label,
            valor_aberto: String(r.openBalance).replace('.', ','),
          }));
    const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = side === 'pay' ? 'vencimentos-pagar.csv' : 'vencimentos-receber.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  if (summaryQ.isLoading) {
    return (
      <div className="finance-page" aria-busy="true" aria-live="polite">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <p className="eyebrow">Nexora · Financeiro</p>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contas a pagar e receber</h1>
          </div>
        </div>
        <div className="kpi-grid">
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
        </div>
        <LoadingSkeleton width="100%" height="300px" />
      </div>
    );
  }

  if (summaryQ.error) {
    return (
      <div className="finance-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <p className="eyebrow">Nexora · Financeiro</p>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contas a pagar e receber</h1>
          </div>
        </div>
        <Card>
          <div className="error-state" role="alert">
            <p>Erro ao carregar o resumo financeiro (GET /finance/dashboards/summary).</p>
            <p className="error-detail">{(summaryQ.error as Error).message}</p>
            <Button variant="secondary" onClick={() => void summaryQ.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="finance-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <p className="eyebrow">Nexora · Financeiro</p>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contas a pagar e receber</h1>
          </div>
        </div>
        <Card>
          <div className="empty-state" role="status">
            <p>Nenhum dado financeiro disponível.</p>
          </div>
        </Card>
      </div>
    );
  }

  const alerts = [
    {
      id: 'pay-overdue',
      title: `${summary.overduePayablesCount} títulos a pagar vencidos`,
      detail: `${formatCurrency(summary.overduePayables)} em atraso`,
      tone: summary.overduePayablesCount > 0 ? 'error' : 'success',
      path: '/finance/payables',
      action: 'Abrir contas a pagar',
    },
    {
      id: 'rec-overdue',
      title: `${summary.overdueReceivablesCount} títulos a receber vencidos`,
      detail: `${formatCurrency(summary.overdueReceivables)} em atraso`,
      tone: summary.overdueReceivablesCount > 0 ? 'warning' : 'success',
      path: '/finance/receivables',
      action: 'Abrir contas a receber',
    },
    {
      id: 'net-position',
      title: `Posição líquida 30 dias: ${formatCurrency(summary.netShortTermPosition)}`,
      detail:
        summary.netShortTermPosition >= 0
          ? 'Cobertura positiva no curto prazo (recebíveis − pagáveis)'
          : 'Atenção: cobertura negativa no curto prazo',
      tone: summary.netShortTermPosition >= 0 ? 'success' : 'error',
      path: '/finance/reconciliation',
      action: 'Abrir conciliação',
    },
    {
      id: 'cash',
      title: `Saldo em caixa: ${formatCurrency(summary.cashBalance)}`,
      detail: 'Soma do saldo atual de todas as contas bancárias',
      tone: 'info',
      path: '/finance/accounts',
      action: 'Abrir contas',
    },
  ] as const;

  return (
    <div className="finance-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Gestão Financeira</h1>
          <p className="tenant-name">
            Visão consolidada de contas a pagar e receber · {tenant?.name ?? 'Operação'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <div className="side-toggle" role="group" aria-label="Período de vencimento">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={period === p.key ? 'active' : ''}
                aria-pressed={period === p.key}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={handleExport}>
            Exportar CSV
          </Button>
        </div>
      </div>

      <div className="filter-row">
        <Input
          label=""
          placeholder="Buscar por contraparte ou número do documento…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="chips" role="group" aria-label="Situação dos títulos">
          {SITUATIONS.map((sit) => (
            <button
              key={sit.key}
              type="button"
              className={situation === sit.key ? 'chip active' : 'chip'}
              aria-pressed={situation === sit.key}
              onClick={() => setSituation(sit.key)}
            >
              {sit.label}
            </button>
          ))}
        </div>
      </div>

      <div className="tabs" role="tablist" aria-label="Seções do financeiro">
        <button type="button" role="tab" aria-selected={tab === 'overview'} className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
          Visão geral
        </button>
        <button type="button" role="tab" aria-selected={tab === 'dre'} className={tab === 'dre' ? 'active' : ''} onClick={() => setTab('dre')}>
          DRE (mês atual)
        </button>
      </div>

      {tab === 'overview' ? (
        <>
          <div className="bento" aria-live="polite">
            <Card>
              <div className="bento-card">
                <div className="bento-head">
                  <span className="bento-label">Saldo consolidado</span>
                  <span className="material-symbols-outlined bento-icon" aria-hidden="true">account_balance</span>
                </div>
                <p className="bento-value">{formatCurrency(summary.cashBalance)}</p>
                <p className="bento-foot">
                  Posição 30 dias {formatCurrency(summary.netShortTermPosition)}
                </p>
                {/* A referência mostra "+5,2% vs mês anterior"; não há saldo do
                    mês anterior em nenhum endpoint, e inventar a variação seria
                    inventar o número que mais importa neste cartão. */}
              </div>
            </Card>

            <Card>
              <div className="bento-card">
                <div className="bento-head">
                  <span className="bento-label">Recebimentos ({PERIOD_LABEL[period]})</span>
                  <span className="material-symbols-outlined bento-icon bento-icon--in" aria-hidden="true">arrow_downward</span>
                </div>
                <p className="bento-value bento-value--in">{formatCurrency(inWindow.receive)}</p>
                <p className="bento-foot">
                  {inWindow.receiveCount} {inWindow.receiveCount === 1 ? 'título' : 'títulos'} · vencido{' '}
                  {formatCurrency(summary.overdueReceivables)}
                </p>
              </div>
            </Card>

            <Card>
              <div className="bento-card">
                <div className="bento-head">
                  <span className="bento-label">Pagamentos ({PERIOD_LABEL[period]})</span>
                  <span className="material-symbols-outlined bento-icon bento-icon--out" aria-hidden="true">arrow_upward</span>
                </div>
                <p className="bento-value bento-value--out">{formatCurrency(inWindow.pay)}</p>
                <p className="bento-foot">
                  {inWindow.payCount} {inWindow.payCount === 1 ? 'título' : 'títulos'} · vencido{' '}
                  {formatCurrency(summary.overduePayables)}
                </p>
              </div>
            </Card>

            <Card>
              <div className="bento-card">
                <div className="bento-head">
                  <span className="bento-label">Conciliação bancária</span>
                  <span className="material-symbols-outlined bento-icon" aria-hidden="true">sync</span>
                </div>
                {reconPct === null ? (
                  <>
                    <p className="bento-value bento-value--muted">—</p>
                    <p className="bento-foot">Nenhuma conciliação iniciada</p>
                  </>
                ) : (
                  <>
                    <p className="bento-value">{reconPct}%</p>
                    <div className="bento-progress" role="img" aria-label={`${reconPct} por cento conciliado`}>
                      <div className="bento-progress__fill" style={{ width: `${reconPct}%` }} />
                    </div>
                    <p className="bento-foot">
                      {recon?.accountName} · {recon?.matchedCount} de {recon?.importedCount} linhas
                    </p>
                  </>
                )}
              </div>
            </Card>
          </div>

          <div className="exec-grid-2">
            <Card>
              <CardHeader
                action={
                  <div className="side-toggle small" role="group" aria-label="Granularidade do fluxo projetado">
                    <button type="button" className={gran === 'weekly' ? 'active' : ''} aria-pressed={gran === 'weekly'} onClick={() => setGran('weekly')}>
                      Semanal
                    </button>
                    <button type="button" className={gran === 'monthly' ? 'active' : ''} aria-pressed={gran === 'monthly'} onClick={() => setGran('monthly')}>
                      Mensal
                    </button>
                  </div>
                }
              >
                <h3 className="exec-title">Fluxo projetado · 30 dias</h3>
                <p className="exec-subtitle">Projeção de entradas e saídas para os próximos 30 dias</p>
              </CardHeader>
              <CardBody>
                <div className="exec-legend" aria-hidden="true">
                  <span className="legend-item">
                    <i className="dot dot-in" /> Entradas projetadas
                  </span>
                  <span className="legend-item">
                    <i className="dot dot-out" /> Saídas projetadas
                  </span>
                </div>
                {cashFlowQ.isLoading ? (
                  <LoadingSkeleton width="100%" height="240px" />
                ) : cashFlowQ.error ? (
                  <p className="error-detail" role="alert">
                    Fluxo indisponível: {(cashFlowQ.error as Error).message}
                  </p>
                ) : flowPoints.length === 0 ? (
                  <p className="empty-detail" role="status">Sem projeção de fluxo para os próximos 30 dias.</p>
                ) : (
                  <div aria-live="polite">
                    <FlowChart points={flowPoints} />
                  </div>
                )}
                <p className="exec-note">
                  Líquido projetado: {formatCurrency(cashFlowQ.data?.netProjected ?? 0)}
                </p>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                action={
                  <div className="side-toggle small" role="group" aria-label="Aging de pagar ou receber">
                    <button
                      type="button"
                      className={side === 'pay' ? 'active' : ''}
                      aria-pressed={side === 'pay'}
                      onClick={() => setSide('pay')}
                    >
                      Pagar
                    </button>
                    <button
                      type="button"
                      className={side === 'receive' ? 'active' : ''}
                      aria-pressed={side === 'receive'}
                      onClick={() => setSide('receive')}
                    >
                      Receber
                    </button>
                  </div>
                }
              >
                <h3 className="exec-title">Aging · {side === 'pay' ? 'pagar' : 'receber'}</h3>
                <p className="exec-subtitle">
                  {overduePct.toFixed(1)}% do total em aberto está vencido
                </p>
                {activeAging.estimated && (
                  <p className="exec-subtitle exec-subtitle--warn">
                    Faixas estimadas a partir dos títulos carregados — o relatório
                    de aging não respondeu
                  </p>
                )}
              </CardHeader>
              <CardBody>
                {(side === 'pay' ? payablesQ.isLoading : receivablesQ.isLoading) ? (
                  <LoadingSkeleton width="100%" height="200px" />
                ) : (
                  <div aria-live="polite">
                    <AgingBars buckets={activeAging.buckets} note={activeAging.estimated ? 'estimado da página atual' : 'buckets do backend'} />
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          <div className="exec-grid-2">
            <Card>
              <CardHeader
                action={
                  <span className="table-count">
                    {titles.length} de {totalTitles} {totalTitles === 1 ? 'título' : 'títulos'}
                  </span>
                }
              >
                <h3 className="exec-title">Títulos do período</h3>
              </CardHeader>
              <CardBody>
                {payablesQ.isLoading || receivablesQ.isLoading ? (
                  <LoadingSkeleton width="100%" height="220px" />
                ) : payablesQ.error || receivablesQ.error ? (
                  <p className="error-detail" role="alert">
                    Erro ao carregar títulos:{' '}
                    {((payablesQ.error ?? receivablesQ.error) as Error).message}
                  </p>
                ) : titles.length === 0 ? (
                  <p className="empty-detail" role="status">
                    Nenhum título com vencimento neste período{q ? ' para esta busca' : ''}.
                  </p>
                ) : (
                  <table className="due-table">
                    <thead>
                      <tr>
                        <th scope="col">Situação</th>
                        <th scope="col">Vencimento</th>
                        <th scope="col">Parceiro / Histórico</th>
                        <th scope="col">Categoria</th>
                        <th scope="col">Documento</th>
                        <th scope="col" className="num">Valor em aberto</th>
                        <th scope="col">
                          <span className="sr-only">Ações</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {titles.map((t) => {
                        const d = daysUntil(t.dueDate, now);
                        const meta = statusMeta(t.status);
                        const destino = t.direction === 'out' ? '/finance/payables' : '/finance/receivables';
                        return (
                          <tr key={`${t.direction}-${t.id}`}>
                            <td>
                              <Badge variant={meta.variant}>{meta.label}</Badge>
                            </td>
                            <td title={`Vencimento em ${formatDate(t.dueDate)}`}>
                              {formatDate(t.dueDate)}
                              <small className="row-sub">{relativeLabel(d)}</small>
                            </td>
                            <td>
                              <strong>{t.party}</strong>
                              <small className="row-sub">
                                {/* Sem a direção, um título a pagar e um a
                                    receber de mesmo valor ficam iguais na
                                    tabela unificada. */}
                                {t.direction === 'out' ? 'A pagar' : 'A receber'}
                                {t.amount !== t.original
                                  ? ` · parcial de ${formatCurrency(t.original)}`
                                  : ''}
                              </small>
                            </td>
                            <td>
                              <span className="cat-tag">{t.category}</span>
                            </td>
                            <td className="doc">{t.documentNumber}</td>
                            <td className={t.direction === 'out' ? 'num amount-out' : 'num amount-in'}>
                              {t.direction === 'out' ? '−' : '+'}
                              {formatCurrency(t.amount)}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="link-action"
                                onClick={() => void navigate({ to: destino })}
                              >
                                Ver
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="exec-title">Alertas e atividades</h3>
                <p className="exec-subtitle">Cada alerta cita o valor e a quantidade que o originaram</p>
              </CardHeader>
              <CardBody>
                <ul className="activity-list">
                  {alerts.map((a) => (
                    <li key={a.id}>
                      <button type="button" className="activity-item" onClick={() => void navigate({ to: a.path })}>
                        <span className="activity-text">
                          <strong>{a.title}</strong>
                          <small>{a.detail}</small>
                        </span>
                        <span className="activity-go" aria-hidden="true">→</span>
                      </button>
                      <span className="activity-action-label">{a.action}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <CardHeader
            action={
              <Button variant="secondary" onClick={() => void navigate({ to: '/accounting/statements' })}>
                Abrir demonstrativo completo
              </Button>
            }
          >
            <h3 className="exec-title">DRE · mês atual</h3>
            <p className="exec-subtitle">Demonstrativo completo em Contabilidade · Demonstrações</p>
          </CardHeader>
          <CardBody>
            {dreQ.isLoading ? (
              <LoadingSkeleton width="100%" height="240px" />
            ) : dreQ.error ? (
              <p className="error-detail" role="alert">DRE indisponível: {(dreQ.error as Error).message}</p>
            ) : !Array.isArray(dreQ.data?.entries) || dreQ.data.entries.length === 0 ? (
              <p className="empty-detail" role="status">Sem lançamentos de DRE no mês atual.</p>
            ) : (
              <>
                <table className="due-table">
                  <tbody>
                    {dreQ.data.entries.map((e, i) => (
                      <tr key={`${e.label}-${i}`} className={e.isSubtotal ? 'dre-subtotal' : ''}>
                        <td style={{ paddingLeft: `${0.5 + e.level * 1.25}rem` }}>{e.label}</td>
                        <td className="num">{formatCurrency(e.value)}</td>
                      </tr>
                    ))}
                    <tr className="dre-total">
                      <td>Resultado líquido</td>
                      <td className={`num ${dreQ.data.netResult >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(dreQ.data.netResult)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="exec-note">Período: {formatDate(dreQ.data.periodStart)} a {formatDate(dreQ.data.periodEnd)}.</p>
              </>
            )}
          </CardBody>
        </Card>
      )}

      <style>{`
        .finance-page { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.25rem; }
        .eyebrow { margin: 0 0 0.25rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: #002045; font-weight: 700; }
        .tenant-name { margin: 0; color: #43474e; font-size: 0.85rem; }
        .header-controls { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
        .bento { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 1rem; margin-bottom: 1.25rem; }
        .bento-card { display: flex; flex-direction: column; gap: 0.25rem; }
        .bento-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
        .bento-label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .bento-icon { font-size: 18px; color: #74777f; }
        .bento-icon--in { color: #1b7f4d; }
        .bento-icon--out { color: #ba1a1a; }
        .bento-value { margin: 0.25rem 0 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 1.5rem; font-weight: 600; color: #191c1e; }
        .bento-value--in { color: #1b7f4d; }
        .bento-value--out { color: #ba1a1a; }
        .bento-value--muted { color: #74777f; }
        .bento-foot { margin: 0.15rem 0 0; font-size: 0.75rem; color: #74777f; }
        .bento-progress { margin-top: 0.4rem; height: 6px; background: #e0e3e5; border-radius: 2px; overflow: hidden; }
        .bento-progress__fill { height: 100%; background: #002045; }

        .chips { display: inline-flex; gap: 0.375rem; flex-wrap: wrap; }
        .chip { padding: 0.3rem 0.7rem; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; cursor: pointer; }
        .chip.active { background: #002045; border-color: #002045; color: #ffffff; }
        .chip:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }

        .table-count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; white-space: nowrap; }
        .cat-tag { display: inline-block; padding: 1px 6px; background: #eceef0; border-radius: 2px; font-size: 0.75rem; color: #43474e; white-space: nowrap; }
        .due-table td.doc { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; white-space: nowrap; }
        .due-table td.amount-out { color: #ba1a1a; }
        .due-table td.amount-in { color: #1b7f4d; }
        .exec-subtitle--warn { color: #7a3f00; }

        .side-toggle { display: inline-flex; background: rgba(25, 28, 30, 0.06); border: 1px solid rgba(25, 28, 30, 0.08); border-radius: 9999px; padding: 3px; gap: 2px; }
        .side-toggle button { border: 0; background: transparent; padding: 0.45rem 1.1rem; border-radius: 9999px; font-size: 0.85rem; font-weight: 600; color: #43474e; cursor: pointer; }
        .side-toggle button.active { background: #191c1e; color: #ffffff; }
        .side-toggle button:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .side-toggle.small button { padding: 0.3rem 0.8rem; font-size: 0.78rem; }
        .filter-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
        .filter-row > :first-child { flex: 1 1 320px; }
        .filter-note { font-size: 0.78rem; color: #43474e; border-bottom: 1px dotted #74777f; cursor: help; }
        .tabs { display: inline-flex; gap: 0.25rem; border-bottom: 2px solid rgba(25, 28, 30, 0.08); }
        .tabs button { border: 0; background: none; padding: 0.6rem 1rem; font-size: 0.9rem; font-weight: 600; color: #43474e; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; }
        .tabs button.active { color: #191c1e; border-bottom-color: #002045; }
        .tabs button:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1rem; }
        .kpi-card { padding: 0.25rem; }
        .kpi-value { margin: 0; font-size: 1.25rem; font-weight: 800; color: #191c1e; }
        .kpi-label { margin: 0.25rem 0; font-size: 12px; color: #43474e; font-weight: 500; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}
        .kpi-source { margin: 0; font-size: 0.72rem; color: #74777f; }
        .exec-grid-2 { display: grid; grid-template-columns: 1.4fr 1fr; gap: 1.25rem; align-items: start; }
        .exec-title { margin: 0; font-size: 1rem; font-weight: 700; color: #191c1e; }
        .exec-subtitle { margin: 0.15rem 0 0; font-size: 0.78rem; color: #43474e; }
        .exec-legend { display: flex; gap: 1rem; margin: 0.75rem 0; font-size: 0.8rem; color: #43474e; flex-wrap: wrap; }
        .legend-item { display: inline-flex; align-items: center; gap: 0.4rem; }
        .dot { width: 10px; height: 10px; border-radius: 9999px; display: inline-block; }
        .dot-in { background: #1a365d; }
        .dot-out { background: #ed6c02; }
        .exec-chart-scroll { overflow-x: auto; }
        .exec-chart { width: 100%; min-width: 460px; height: auto; display: block; }
        .exec-grid { stroke: rgba(25, 28, 30, 0.1); stroke-width: 1; }
        .exec-grid-label { font-size: 10px; fill: #74777f; }
        .exec-x-label { font-size: 12px; fill: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .exec-bar-in { fill: #1a365d; }
        .exec-bar-out { fill: #ed6c02; opacity: 0.9; }
        .exec-note { font-size: 0.78rem; color: #43474e; margin: 0.75rem 0 0; }
        .due-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .due-table th { text-align: left; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: #43474e; padding: 0.5rem; border-bottom: 1px solid rgba(25, 28, 30, 0.1); position: sticky; top: 0; z-index: 1;}
        .due-table td { padding: 0.55rem 0.5rem; border-bottom: 1px solid rgba(25, 28, 30, 0.06); color: #191c1e; vertical-align: middle; }
        .due-table .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; font-family: "JetBrains Mono", ui-monospace, monospace;}
        .due-table .row-sub { display: block; color: #43474e; font-size: 0.75rem; }
        .due-table .dre-subtotal td { font-weight: 700; background: rgba(25, 28, 30, 0.03); }
        .due-table .dre-total td { font-weight: 800; font-size: 0.95rem; border-top: 2px solid #191c1e; }
        .positive { color: #1b4f1e; }
        .negative { color: #ba1a1a; }
        .link-action { border: 0; background: none; color: #002045; font-weight: 600; font-size: 0.85rem; cursor: pointer; padding: 0.25rem 0.5rem; border-radius: 4px; }
        .link-action:hover { background: rgba(2,132,199,0.1); }
        .link-action:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .activity-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .activity-list li { border-bottom: 1px solid rgba(25, 28, 30, 0.07); padding: 0.25rem 0; }
        .activity-item { width: 100%; display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 0.5rem; background: none; border: 0; cursor: pointer; text-align: left; border-radius: 8px; }
        .activity-item:hover { background: rgba(0, 32, 69, 0.07); }
        .activity-item:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .activity-text { display: flex; flex-direction: column; gap: 2px; flex: 1; }
        .activity-text strong { font-size: 0.88rem; color: #191c1e; }
        .activity-text small { font-size: 0.78rem; color: #43474e; }
        .activity-go { color: #74777f; font-size: 1.1rem; }
        .activity-action-label { font-size: 12px; color: #43474e; padding-left: 0.5rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .error-state { padding: 2rem; text-align: center; color: #ba1a1a; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .error-detail { font-size: 0.85rem; color: #ba1a1a; }
        .empty-state { padding: 2rem; text-align: center; color: #43474e; }
        .empty-detail { font-size: 0.85rem; color: #43474e; }
        .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
        @media (max-width: 1080px) { .exec-grid-2 { grid-template-columns: 1fr; } }
        @media (max-width: 640px) { .finance-page { padding: 1rem; } .due-table .row-sub { display: none; } }
        @media (prefers-reduced-motion: reduce) { .activity-item, .side-toggle button, .tabs button { transition: none; } }
      `}</style>
    </div>
  );
}
